/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { TextField } from "@mui/material";
import { selectEditMode } from "../slices/editMode/editModeSlice";
import { resourceUpdated, selectResourceById } from "../slices/resourceCollection/resourceCollectionSlice";
import updateElement from "../functions/updateElement";
import jsonEquals from "../functions/jsonEquals";
import DatatypeSelector from "../datatypeComponents/DatatypeSelector";
import StringEntry from "../datatypeComponents/StringEntry";
import ArrayEntry from "../datatypeComponents/ArrayEntry";
import CodeEntry from "../datatypeComponents/CodeEntry";
import CodingEntry from "../datatypeComponents/CodingEntry";
import BooleanEntry from "../datatypeComponents/BooleanEntry";
import PeriodEntry, { DateEntry, DateTimeEntry } from "../datatypeComponents/PeriodEntry";
import ReferenceEntry from "../datatypeComponents/ReferenceEntry";
import MarkdownEntry from "../datatypeComponents/MarkdownEntry";
import CodeArrayEntry from "../datatypeComponents/CodeArrayEntry";
import CodeableConceptEntry from "../datatypeComponents/CodeableConceptEntry";
import Spinner from "../components/Spinner";
import { compositionTypeCodeableConceptSet } from "../dictionaries/ProfilesByResourceType";
import { convertValueSetToCodeableConceptSet } from "../dictionaries/THOandFHIRValueSets";
import { languageCodes } from "../dictionaries/CodeSystemLookup";
import { displayStringFromFHIRHumanName } from "../datatypeComponents/HumanNameEntry";
import ResourceStatusRadioButton from "./resourceDisplayCommonComponents/ResourceStatusRadioButton";

const metadataDotStatusValues = ['draft', 'active', 'retired', 'unknown'];
const compositionDotStatusValues = ['registered', 'partial', 'preliminary', 'final', 'amended', 'corrected',
    'appended', 'cancelled', 'entered-in-error', 'deprecated', 'unknown'];
const compositionDotAuthorResourceTypes = ['Practitioner', 'PractitionerRole', 'Device', 'Patient', 'RelatedPerson', 'Organization'];
const listDotSourceResourceTypes = ['Practitioner', 'PractitionerRole', 'Device', 'Patient', 'RelatedPerson', 'Organization', 'CareTeam'];
const versionAlgorithmAllowedDatatypes = ["string", "Coding"];
const versionAlgorithmValueSet = [
    { system: "http://hl7.org/fhir/version-algorithm", code: "major-minor", display: "Major/Minor" },
    { system: "http://hl7.org/fhir/version-algorithm", code: "semver", display: "SemVer" },
    { system: "http://hl7.org/fhir/version-algorithm", code: "integer", display: "Integer" },
    { system: "http://hl7.org/fhir/version-algorithm", code: "alpha", display: "Alphabetical" },
    { system: "http://hl7.org/fhir/version-algorithm", code: "date", display: "Date" },
    { system: "http://hl7.org/fhir/version-algorithm", code: "natural", display: "Natural" }
];

const changeMetadataElementBooleansForResourceType = (resourceType, metadataElementSettings) => {
    if (resourceType === "ArtifactAssessment") {
        //metadataElementSettings.nameAsMetadataElement = false;
        //metadataElementSettings.urlAsMetadataElement = false;
        //metadataElementSettings.versionAsMetadataElement = false;
        //metadataElementSettings.versionAlgorithmAsMetadataElement = false;
        //metadataElementSettings.statusAsMetadataElement = false;
        //metadataElementSettings.experimentalAsMetadataElement = false;
        //metadataElementSettings.publisherAsMetadataElement = false;
        //metadataElementSettings.contactAsMetadataElement = false;
        //metadataElementSettings.descriptionAsMetadataElement = false;
        //metadataElementSettings.useContextAsMetadataElement = false;
        //metadataElementSettings.jurisdictionAsMetadataElement = false;
        //metadataElementSettings.purposeAsMetadataElement = false;
        //metadataElementSettings.copyrightLabelAsMetadataElement = false;
        metadataElementSettings.topicAsMetadataElement = false;
        //metadataElementSettings.authorAsMetadataElement = false;
        //metadataElementSettings.reviewerAsMetadataElement = false;
        //metadataElementSettings.editorAsMetadataElement = false;
        //metadataElementSettings.endorserAsMetadataElement = false;
        metadataElementSettings.recorderAsMetadataElement = false;
        metadataElementSettings.relatedArtifactAsMetadataElement = false;
        //metadataElementSettings.effectivePeriodAsMetadataElement = false;
        metadataElementSettings.publicationDateAsMetadataElementAllowed = true;
    } else if (resourceType === "Composition") {
        metadataElementSettings.versionAlgorithmAsMetadataElement = false;
        metadataElementSettings.experimentalAsMetadataElement = false;
        metadataElementSettings.descriptionAsMetadataElement = false;
        metadataElementSettings.jurisdictionAsMetadataElement = false;
        metadataElementSettings.purposeAsMetadataElement = false;
        metadataElementSettings.copyrightAsMetadataElement = false;
        metadataElementSettings.copyrightLabelAsMetadataElement = false;
        metadataElementSettings.approvalDateAsMetadataElement = false;
        metadataElementSettings.lastReviewDateAsMetadataElement = false;
        metadataElementSettings.effectivePeriodAsMetadataElement = false;
        metadataElementSettings.relatedArtifactAsMetadataElement = false;
        metadataElementSettings.publicationDateAsMetadataElementAllowed = true;
        metadataElementSettings.additionalLanguageAllowed = true;
    } else if (resourceType === "DocumentReference") {
        metadataElementSettings.titleAsMetadataElement = false;
        metadataElementSettings.urlAsMetadataElement = false;
        metadataElementSettings.nameAsMetadataElement = false;
        metadataElementSettings.versionAlgorithmAsMetadataElement = false;
        metadataElementSettings.experimentalAsMetadataElement = false;
        metadataElementSettings.jurisdictionAsMetadataElement = false;
        metadataElementSettings.purposeAsMetadataElement = false;
        metadataElementSettings.copyrightAsMetadataElement = false;
        metadataElementSettings.copyrightLabelAsMetadataElement = false;
        metadataElementSettings.approvalDateAsMetadataElement = false;
        metadataElementSettings.lastReviewDateAsMetadataElement = false;
        metadataElementSettings.effectivePeriodAsMetadataElement = false;
        metadataElementSettings.relatedArtifactAsMetadataElement = false;
    } else if (resourceType === "Evidence") {
        metadataElementSettings.jurisdictionAsMetadataElement = false;
        metadataElementSettings.relatedArtifactAsMetadataElement = false;
        metadataElementSettings.publicationDateAsMetadataElementAllowed = true;
    } else if (resourceType === "EvidenceVariable") {
        metadataElementSettings.jurisdictionAsMetadataElement = false;
        metadataElementSettings.relatedArtifactAsMetadataElement = false;
        metadataElementSettings.publicationDateAsMetadataElementAllowed = true;
    } else if (resourceType === "Group") {
        metadataElementSettings.topicAsMetadataElement = false;
        metadataElementSettings.authorAsMetadataElement = false;
        metadataElementSettings.reviewerAsMetadataElement = false;
        metadataElementSettings.editorAsMetadataElement = false;
        metadataElementSettings.endorserAsMetadataElement = false;
        metadataElementSettings.recorderAsMetadataElement = false;
        metadataElementSettings.relatesToAsMetadataElement = false;
        metadataElementSettings.relatedArtifactAsMetadataElement = false;
        metadataElementSettings.approvalDateAsMetadataElement = false;
        metadataElementSettings.lastReviewDateAsMetadataElement = false;
        metadataElementSettings.effectivePeriodAsMetadataElement = false;
        metadataElementSettings.jurisdictionAsMetadataElement = false;
        metadataElementSettings.publicationDateAsMetadataElementAllowed = true;
    } else if (resourceType === "List") {
        metadataElementSettings.nameAsMetadataElement = false;
        metadataElementSettings.urlAsMetadataElement = false;
        metadataElementSettings.versionAsMetadataElement = false;
        metadataElementSettings.versionAlgorithmAsMetadataElement = false;
        metadataElementSettings.experimentalAsMetadataElement = false;
        metadataElementSettings.publisherAsMetadataElement = false;
        metadataElementSettings.contactAsMetadataElement = false;
        metadataElementSettings.descriptionAsMetadataElement = false;
        metadataElementSettings.useContextAsMetadataElement = false;
        metadataElementSettings.jurisdictionAsMetadataElement = false;
        metadataElementSettings.purposeAsMetadataElement = false;
        metadataElementSettings.copyrightLabelAsMetadataElement = false;
        metadataElementSettings.topicAsMetadataElement = false;
        metadataElementSettings.authorAsMetadataElement = false;
        metadataElementSettings.reviewerAsMetadataElement = false;
        metadataElementSettings.editorAsMetadataElement = false;
        metadataElementSettings.endorserAsMetadataElement = false;
        metadataElementSettings.recorderAsMetadataElement = false;
        metadataElementSettings.relatesToAsMetadataElement = false;
        metadataElementSettings.relatedArtifactAsMetadataElement = false;
        metadataElementSettings.approvalDateAsMetadataElement = false;
        metadataElementSettings.lastReviewDateAsMetadataElement = false;
        metadataElementSettings.effectivePeriodAsMetadataElement = false;
    } else if (resourceType === "ResearchStudy") {
        metadataElementSettings.versionAlgorithmAsMetadataElement = false;
        metadataElementSettings.experimentalAsMetadataElement = false;
        metadataElementSettings.publisherAsMetadataElement = false;
        metadataElementSettings.contactAsMetadataElement = false;
        metadataElementSettings.useContextAsMetadataElement = false;
        metadataElementSettings.jurisdictionAsMetadataElement = false;
        metadataElementSettings.purposeAsMetadataElement = false;
        metadataElementSettings.copyrightAsMetadataElement = false;
        metadataElementSettings.copyrightLabelAsMetadataElement = false;
        metadataElementSettings.topicAsMetadataElement = false;
        metadataElementSettings.authorAsMetadataElement = false;
        metadataElementSettings.reviewerAsMetadataElement = false;
        metadataElementSettings.editorAsMetadataElement = false;
        metadataElementSettings.endorserAsMetadataElement = false;
        metadataElementSettings.recorderAsMetadataElement = false;
        metadataElementSettings.approvalDateAsMetadataElement = false;
        metadataElementSettings.lastReviewDateAsMetadataElement = false;
        metadataElementSettings.effectivePeriodAsMetadataElement = false;
        metadataElementSettings.relatedArtifactAsMetadataElement = false;
        metadataElementSettings.publicationDateAsMetadataElementAllowed = true;
    } else if (resourceType === "ResearchSubject") {
        metadataElementSettings.titleAsMetadataElement = false;
        metadataElementSettings.nameAsMetadataElement = false;
        metadataElementSettings.urlAsMetadataElement = false;
        metadataElementSettings.dateAsMetadataElement = false;
        metadataElementSettings.versionAsMetadataElement = false;
        metadataElementSettings.versionAlgorithmAsMetadataElement = false;
        metadataElementSettings.experimentalAsMetadataElement = false;
        metadataElementSettings.publisherAsMetadataElement = false;
        metadataElementSettings.contactAsMetadataElement = false;
        metadataElementSettings.descriptionAsMetadataElement = false;
        metadataElementSettings.useContextAsMetadataElement = false;
        metadataElementSettings.jurisdictionAsMetadataElement = false;
        metadataElementSettings.purposeAsMetadataElement = false;
        metadataElementSettings.copyrightLabelAsMetadataElement = false;
        metadataElementSettings.topicAsMetadataElement = false;
        metadataElementSettings.authorAsMetadataElement = false;
        metadataElementSettings.reviewerAsMetadataElement = false;
        metadataElementSettings.editorAsMetadataElement = false;
        metadataElementSettings.endorserAsMetadataElement = false;
        metadataElementSettings.recorderAsMetadataElement = false;
        metadataElementSettings.relatesToAsMetadataElement = false;
        metadataElementSettings.relatedArtifactAsMetadataElement = false;
        metadataElementSettings.approvalDateAsMetadataElement = false;
        metadataElementSettings.lastReviewDateAsMetadataElement = false;
        metadataElementSettings.effectivePeriodAsMetadataElement = false;
    } else if (resourceType === "ImplementationGuide") {
        metadataElementSettings.topicAsMetadataElement = false;
        metadataElementSettings.authorAsMetadataElement = false;
        metadataElementSettings.reviewerAsMetadataElement = false;
        metadataElementSettings.editorAsMetadataElement = false;
        metadataElementSettings.endorserAsMetadataElement = false;
        metadataElementSettings.recorderAsMetadataElement = false;
        metadataElementSettings.approvalDateAsMetadataElement = false;
        metadataElementSettings.lastReviewDateAsMetadataElement = false;
        metadataElementSettings.effectivePeriodAsMetadataElement = false;
        metadataElementSettings.relatedArtifactAsMetadataElement = false;
        metadataElementSettings.publicationDateAsMetadataElementAllowed = true;
    } else if (resourceType === "Citation") {
        metadataElementSettings.publicationDateAsMetadataElementAllowed = true;
    } else if (resourceType === "Medication" || resourceType === "Substance" || resourceType === "ActivityDefinition" ||
        resourceType === "PlanDefinition" || resourceType === "Library" ||
        resourceType === "CodeSystem" || resourceType === "ValueSet" || resourceType === "Measure" ||
        resourceType === "Questionnaire" || resourceType === "ConceptMap" || resourceType === "NamingSystem") {
        metadataElementSettings.relatesToAsMetadataElement = false;
        metadataElementSettings.publicationDateAsMetadataElementAllowed = true;
    }
}

const MetaProfileEntry = ({ resourceType, startingValue, setResourceState }) => {

    const [metaState, setMetaState] = useState({});

    useEffect(() => {
        let startingMeta = { "versionId": "", "lastUpdated": "", "source": "", "profile": [], "security": [], "tag": [] };
        if (startingValue) {
            if (startingValue.versionId) { startingMeta.versionId = startingValue.versionId; }
            if (startingValue.lastUpdated) { startingMeta.lastUpdated = startingValue.lastUpdated; }
            if (startingValue.source) { startingMeta.source = startingValue.source; }
            if (startingValue.profile) { startingMeta.profile = startingValue.profile; }
            if (startingValue.security) { startingMeta.security = startingValue.security; }
            if (startingValue.tag) { startingMeta.tag = startingValue.tag; }
        }
        setMetaState(JSON.parse(JSON.stringify(startingMeta)));
    }, []);

    useEffect((() => {
        if (Object.keys(metaState).length) {
            let newMeta = {};
            if (metaState.versionId) { newMeta.versionId = metaState.versionId; }
            if (metaState.lastUpdated) { newMeta.lastUpdated = metaState.lastUpdated; }
            if (metaState.source) { newMeta.source = metaState.source; }
            if (Array.isArray(metaState.profile) && metaState.profile.length > 0) {
                newMeta.profile = metaState.profile;
            }
            if (Array.isArray(metaState.security) && metaState.security.length > 0) {
                newMeta.security = metaState.security;
            }
            if (Array.isArray(metaState.tag) && metaState.tag.length > 0) {
                newMeta.tag = metaState.tag;
            }
            setResourceState(prevState => { return { ...prevState, "meta": newMeta }; });
        }
    }), [metaState]);

    return <>
        {metaState.profile &&
            <ArrayEntry datatype='Profile' elementName='profile' fieldLabel='Profile'
                resourceType={resourceType} deletable={true} deletableArray={true}
                startEmptyArrayClosed={true}
                startingValue={metaState.profile} setResourceState={setMetaState} />}
    </>
};

const updateResourceFromExtensions = (dispatch, extensionState,
    resourceState, setResourceState, parentResourceDetail) => {
    let newExtension = [];
    if (extensionState.otherExtensions) {
        newExtension = JSON.parse(JSON.stringify(extensionState.otherExtensions));
    }
    if (extensionState.title) {
        newExtension.push({
            "url": "http://hl7.org/fhir/StructureDefinition/artifact-title",
            "valueString": extensionState.title
        });
    }
    if (extensionState.name) {
        newExtension.push({
            "url": "http://hl7.org/fhir/StructureDefinition/artifact-name",
            "valueString": extensionState.name
        });
    }
    if (extensionState.url) {
        newExtension.push({
            "url": "http://hl7.org/fhir/StructureDefinition/artifact-url",
            "valueUri": extensionState.url
        });
    }
    if (extensionState.identifier?.length > 0) {
        for (const identifierInstance of extensionState.identifier) {
            newExtension.push({
                "url": "http://hl7.org/fhir/StructureDefinition/artifact-identifier",
                "valueIdentifier": JSON.parse(JSON.stringify(identifierInstance))
            });
        }
    }
    if (extensionState.additionalLanguage?.length > 0) {
        for (const languageInstance of extensionState.additionalLanguage) {
            newExtension.push({
                "url": "http://hl7.org/fhir/StructureDefinition/additional-language",
                "valueCode": languageInstance
            });
        }
    }
    if (extensionState.version) {
        newExtension.push({
            "url": "http://hl7.org/fhir/StructureDefinition/artifact-version",
            "valueString": extensionState.version
        });
    }
    if (extensionState.versionAlgorithmString) {
        newExtension.push({
            "url": "http://hl7.org/fhir/StructureDefinition/artifact-versionAlgorithm",
            "valueString": extensionState.versionAlgorithmString
        });
    }
    if (extensionState.status) {
        newExtension.push({
            "url": "http://hl7.org/fhir/StructureDefinition/artifact-status",
            "valueCode": extensionState.status
        });
    }
    if (typeof extensionState.experimental === "boolean") {
        newExtension.push({
            "url": "http://hl7.org/fhir/StructureDefinition/artifact-experimental",
            "valueBoolean": extensionState.experimental
        });
    }
    if (extensionState.date) {
        newExtension.push({
            "url": "http://hl7.org/fhir/StructureDefinition/artifact-date",
            "valueDateTime": extensionState.date
        });
    }
    if (extensionState.publisher) {
        newExtension.push({
            "url": "http://hl7.org/fhir/StructureDefinition/artifact-publisher",
            "valueString": extensionState.publisher
        });
    }
    if (extensionState.contact?.length > 0) {
        for (const instance of extensionState.contact) {
            newExtension.push({
                "url": "http://hl7.org/fhir/StructureDefinition/artifact-contact",
                "valueContactDetail": JSON.parse(JSON.stringify(instance))
            });
        }
    }
    if (extensionState.extendedContact?.length > 0) {
        for (const instance of extensionState.extendedContact) {
            newExtension.push({
                "url": "http://hl7.org/fhir/StructureDefinition/artifact-extended-contact-detail",
                "valueExtendedContactDetail": JSON.parse(JSON.stringify(instance))
            });
        }
    }
    if (extensionState.description) {
        newExtension.push({
            "url": "http://hl7.org/fhir/StructureDefinition/artifact-description",
            "valueMarkdown": extensionState.description
        });
    }
    if (extensionState.useContext?.length > 0) {
        for (const instance of extensionState.useContext) {
            newExtension.push({
                "url": "http://hl7.org/fhir/StructureDefinition/artifact-useContext",
                "valueUsageContext": JSON.parse(JSON.stringify(instance))
            });
        }
    }
    if (extensionState.jurisdiction?.length > 0) {
        for (const instance of extensionState.jurisdiction) {
            newExtension.push({
                "url": "http://hl7.org/fhir/StructureDefinition/artifact-jurisdiction",
                "valueCodeableConcept": JSON.parse(JSON.stringify(instance))
            });
        }
    }
    if (extensionState.purpose) {
        newExtension.push({
            "url": "http://hl7.org/fhir/StructureDefinition/artifact-purpose",
            "valueMarkdown": extensionState.purpose
        });
    }
    if (extensionState.copyright) {
        newExtension.push({
            "url": "http://hl7.org/fhir/StructureDefinition/artifact-copyright",
            "valueMarkdown": extensionState.copyright
        });
    }
    if (extensionState.copyrightLabel) {
        newExtension.push({
            "url": "http://hl7.org/fhir/StructureDefinition/artifact-copyrightLabel",
            "valueString": extensionState.copyrightLabel
        });
    }
    if (extensionState.topic?.length > 0) {
        for (const instance of extensionState.topic) {
            newExtension.push({
                "url": "http://hl7.org/fhir/StructureDefinition/artifact-topic",
                "valueCodeableConcept": JSON.parse(JSON.stringify(instance))
            });
        }
    }
    if (extensionState.author?.length > 0) {
        for (const instance of extensionState.author) {
            newExtension.push({
                "url": "http://hl7.org/fhir/StructureDefinition/artifact-author",
                "valueContactDetail": JSON.parse(JSON.stringify(instance))
            });
        }
    }
    if (extensionState.reviewer?.length > 0) {
        for (const instance of extensionState.reviewer) {
            newExtension.push({
                "url": "http://hl7.org/fhir/StructureDefinition/artifact-reviewer",
                "valueContactDetail": JSON.parse(JSON.stringify(instance))
            });
        }
    }
    if (extensionState.editor?.length > 0) {
        for (const instance of extensionState.editor) {
            newExtension.push({
                "url": "http://hl7.org/fhir/StructureDefinition/artifact-editor",
                "valueContactDetail": JSON.parse(JSON.stringify(instance))
            });
        }
    }
    if (extensionState.endorser?.length > 0) {
        for (const instance of extensionState.endorser) {
            newExtension.push({
                "url": "http://hl7.org/fhir/StructureDefinition/artifact-endorser",
                "valueContactDetail": JSON.parse(JSON.stringify(instance))
            });
        }
    }
    if (extensionState.recorder?.length > 0) {
        for (const instance of extensionState.recorder) {
            newExtension.push({
                "url": "http://hl7.org/fhir/uv/ebm/StructureDefinition/artifact-recorder",
                "valueContactDetail": JSON.parse(JSON.stringify(instance))
            });
        }
    }
    if (extensionState.relatedArtifact?.length > 0) {
        for (const instance of extensionState.relatedArtifact) {
            newExtension.push({
                "url": "http://hl7.org/fhir/StructureDefinition/artifact-relatedArtifact",
                "valueRelatedArtifact": JSON.parse(JSON.stringify(instance))
            });
        }
    }
    if (extensionState.relatesTo?.length > 0) {
        for (const instance of extensionState.relatesTo) {
            newExtension.push({
                "url": "http://hl7.org/fhir/uv/ebm/StructureDefinition/relates-to",
                "extension": JSON.parse(JSON.stringify(instance))
            });
        }
    }
    if (extensionState.approvalDate) {
        newExtension.push({
            "url": "http://hl7.org/fhir/StructureDefinition/resource-approvalDate",
            "valueDate": extensionState.approvalDate
        });
    }
    if (extensionState.lastReviewDate) {
        newExtension.push({
            "url": "http://hl7.org/fhir/StructureDefinition/resource-lastReviewDate",
            "valueDate": extensionState.lastReviewDate
        });
    }
    if (extensionState.publicationDate) {
        newExtension.push({
            "url": "http://hl7.org/fhir/StructureDefinition/artifact-publicationDate",
            "valueDate": extensionState.publicationDate
        });
    }
    if (extensionState.publicationStatus) {
        newExtension.push({
            "url": "http://hl7.org/fhir/uv/ebm/StructureDefinition/artifact-publication-status",
            "valueCodeableConcept": JSON.parse(JSON.stringify(extensionState.publicationStatus))
        });
    }
    if (extensionState.effectivePeriod) {
        newExtension.push({
            "url": "http://hl7.org/fhir/StructureDefinition/resource-effectivePeriod",
            "valuePeriod": JSON.parse(JSON.stringify(extensionState.effectivePeriod))
        });
    }
    if (newExtension.length > 0) {
        setResourceState(prevState => { return { ...prevState, "extension": newExtension }; });
    } else {
        setResourceState(prevState => { return { ...prevState, "extension": null }; });
    }
    let newResourceDetail = JSON.parse(JSON.stringify(parentResourceDetail));
    newResourceDetail.revisedResourceJson = JSON.parse(JSON.stringify(resourceState));
    if (newExtension?.length) {
        newResourceDetail.revisedResourceJson.extension = JSON.parse(JSON.stringify(newExtension));
    } else {
        delete newResourceDetail.revisedResourceJson.extension;
    }
    console.log("dispatch resourceUpdated");
    dispatch(resourceUpdated(newResourceDetail));
};

const MetadataPatternEdit = ({ foi, setJsonViewEditingState, setStatusChanged }) => {

    const dispatch = useDispatch();
    const editMode = useSelector(state => selectEditMode(state));
    const parentResourceDetail = useSelector(state => selectResourceById(state, foi));
    const resourceJson = parentResourceDetail?.revisedResourceJson;
    const resourceType = resourceJson.resourceType;

    let metadataElementSettings = {
        titleAsMetadataElement: true,
        nameAsMetadataElement: true,
        urlAsMetadataElement: true,
        identifierAsMetadataElement: true,
        versionAsMetadataElement: true,
        versionAlgorithmAsMetadataElement: true,
        statusAsMetadataElement: true,
        experimentalAsMetadataElement: true,
        dateAsMetadataElement: true,
        publisherAsMetadataElement: true,
        contactAsMetadataElement: true,
        descriptionAsMetadataElement: true,
        useContextAsMetadataElement: true,
        jurisdictionAsMetadataElement: true,
        purposeAsMetadataElement: true,
        copyrightAsMetadataElement: true,
        copyrightLabelAsMetadataElement: true,
        topicAsMetadataElement: true,
        authorAsMetadataElement: true,
        reviewerAsMetadataElement: true,
        editorAsMetadataElement: true,
        endorserAsMetadataElement: true,
        recorderAsMetadataElement: true,
        relatedArtifactAsMetadataElement: true,
        relatesToAsMetadataElement: true,
        approvalDateAsMetadataElement: true,
        lastReviewDateAsMetadataElement: true,
        effectivePeriodAsMetadataElement: true,
        publicationDateAsMetadataElementAllowed: false,
        publicationStatusAsMetadataElementAllowed: true,
        additionalLanguageAllowed: false
    }
    changeMetadataElementBooleansForResourceType(resourceType, metadataElementSettings);

    const [resourceState, setResourceState] = useState({});
    const [extensionState, setExtensionState] = useState({});
    const [startingExtensionState, setStartingExtensionState] = useState({});
    const [urlChanged, setUrlChanged] = useState(false);
    const [versionAlgorithmDatatypeState, setVersionAlgorithmDatatypeState] = useState('none');
    const [containedResourceDictionary, setContainedResourceDictionary] = useState({});

    useEffect(() => {
        if (resourceJson) {
            setResourceState(resourceJson);
            let startingExtension = resourceJson.extension || [];
            let startingExtensionStateValue = {
                "otherExtensions": [], "identifier": [], "additionalLanguage": [], "contact": [], "extendedContact": [],
                "useContext": [], "jurisdiction": [], "topic": [],
                "author": [], "reviewer": [], "editor": [], "endorser": [], "recorder": [],
                "relatedArtifact": [], "relatesTo": []
            }
            if (Array.isArray(startingExtension) && startingExtension.length > 0) {
                for (const extension of startingExtension) {
                    switch (extension.url) {
                        case 'http://hl7.org/fhir/StructureDefinition/artifact-title':
                            startingExtensionStateValue.title = extension.valueString;
                            break;
                        case 'http://hl7.org/fhir/StructureDefinition/artifact-name':
                            startingExtensionStateValue.name = extension.valueString;
                            break;
                        case 'http://hl7.org/fhir/StructureDefinition/artifact-url':
                            startingExtensionStateValue.url = extension.valueUri;
                            break;
                        case 'http://hl7.org/fhir/StructureDefinition/artifact-identifier':
                            startingExtensionStateValue.identifier.push(extension.valueIdentifier);
                            break;
                        case 'http://hl7.org/fhir/StructureDefinition/additional-language':
                            startingExtensionStateValue.additionalLanguage.push(extension.valueCode);
                            break;
                        case 'http://hl7.org/fhir/StructureDefinition/artifact-version':
                            startingExtensionStateValue.version = extension.valueString;
                            break;
                        case 'http://hl7.org/fhir/StructureDefinition/artifact-versionAlgorithm':
                            startingExtensionStateValue.versionAlgorithmString = extension.valueString;
                            break;
                        case 'http://hl7.org/fhir/StructureDefinition/artifact-status':
                            startingExtensionStateValue.status = extension.valueCode;
                            break;
                        case 'http://hl7.org/fhir/StructureDefinition/artifact-experimental':
                            startingExtensionStateValue.experimental = extension.valueBoolean;
                            break;
                        case 'http://hl7.org/fhir/StructureDefinition/artifact-date':
                            startingExtensionStateValue.date = extension.valueDateTime;
                            break;
                        case 'http://hl7.org/fhir/StructureDefinition/artifact-publisher':
                            startingExtensionStateValue.publisher = extension.valueString;
                            break;
                        case 'http://hl7.org/fhir/StructureDefinition/artifact-contact':
                            startingExtensionStateValue.contact.push(extension.valueContactDetail);
                            break;
                        case 'http://hl7.org/fhir/StructureDefinition/artifact-extended-contact-detail':
                            startingExtensionStateValue.extendedContact.push(extension.valueExtendedContactDetail);
                            break;
                        case 'http://hl7.org/fhir/StructureDefinition/artifact-description':
                            startingExtensionStateValue.description = extension.valueMarkdown;
                            break;
                        case 'http://hl7.org/fhir/StructureDefinition/artifact-useContext':
                            startingExtensionStateValue.useContext.push(extension.valueUsageContext);
                            break;
                        case 'http://hl7.org/fhir/StructureDefinition/artifact-jurisdiction':
                            startingExtensionStateValue.jurisdiction.push(extension.valueCodeableConcept);
                            break;
                        case 'http://hl7.org/fhir/StructureDefinition/artifact-purpose':
                            startingExtensionStateValue.purpose = extension.valueMarkdown;
                            break;
                        case 'http://hl7.org/fhir/StructureDefinition/artifact-copyright':
                            startingExtensionStateValue.copyright = extension.valueMarkdown;
                            break;
                        case 'http://hl7.org/fhir/StructureDefinition/artifact-copyrightLabel':
                            startingExtensionStateValue.copyrightLabel = extension.valueString;
                            break;
                        case 'http://hl7.org/fhir/StructureDefinition/resource-approvalDate':
                            startingExtensionStateValue.approvalDate = extension.valueDate;
                            break;
                        case 'http://hl7.org/fhir/StructureDefinition/resource-lastReviewDate':
                            startingExtensionStateValue.lastReviewDate = extension.valueDate;
                            break;
                        case 'http://hl7.org/fhir/StructureDefinition/artifact-publicationDate':
                            startingExtensionStateValue.publicationDate = extension.valueDate;
                            break;
                        case 'http://hl7.org/fhir/uv/ebm/StructureDefinition/artifact-publication-status':
                            startingExtensionStateValue.publicationStatus = extension.valueCodeableConcept;
                            break;
                        case 'http://hl7.org/fhir/StructureDefinition/resource-effectivePeriod':
                            startingExtensionStateValue.effectivePeriod = extension.valuePeriod;
                            break;
                        case 'http://hl7.org/fhir/StructureDefinition/artifact-approvalDate':
                            startingExtensionStateValue.approvalDate = extension.valueDate;
                            break;
                        case 'http://hl7.org/fhir/StructureDefinition/artifact-lastReviewDate':
                            startingExtensionStateValue.lastReviewDate = extension.valueDate;
                            break;
                        case 'http://hl7.org/fhir/StructureDefinition/artifact-effectivePeriod':
                            startingExtensionStateValue.effectivePeriod = extension.valuePeriod;
                            break;
                        case 'http://hl7.org/fhir/StructureDefinition/artifact-topic':
                            startingExtensionStateValue.topic.push(extension.valueCodeableConcept);
                            break;
                        case 'http://hl7.org/fhir/StructureDefinition/artifact-author':
                            startingExtensionStateValue.author.push(extension.valueContactDetail);
                            break;
                        case 'http://hl7.org/fhir/StructureDefinition/artifact-reviewer':
                            startingExtensionStateValue.reviewer.push(extension.valueContactDetail);
                            break;
                        case 'http://hl7.org/fhir/StructureDefinition/artifact-editor':
                            startingExtensionStateValue.editor.push(extension.valueContactDetail);
                            break;
                        case 'http://hl7.org/fhir/StructureDefinition/artifact-endorser':
                            startingExtensionStateValue.endorser.push(extension.valueContactDetail);
                            break;
                        case 'http://hl7.org/fhir/StructureDefinition/artifact-recorder':
                            startingExtensionStateValue.recorder.push(extension.valueContactDetail);
                            break;
                        case 'http://hl7.org/fhir/uv/ebm/StructureDefinition/artifact-recorder':
                            startingExtensionStateValue.recorder.push(extension.valueContactDetail);
                            break;
                        case 'http://hl7.org/fhir/StructureDefinition/artifact-relatedArtifact':
                            startingExtensionStateValue.relatedArtifact.push(extension.valueRelatedArtifact);
                            break;
                        case 'http://hl7.org/fhir/uv/ebm/StructureDefinition/relates-to':
                            startingExtensionStateValue.relatesTo.push(extension.extension);
                            break;
                        default:
                            startingExtensionStateValue.otherExtensions.push(extension)
                    }
                }
            }
            setExtensionState(startingExtensionStateValue);
            setStartingExtensionState(startingExtensionStateValue);
            if (resourceJson.versionAlgorithmString) {
                setVersionAlgorithmDatatypeState("string");
            }
            if (resourceJson.versionAlgorithmCoding) {
                setVersionAlgorithmDatatypeState("Coding");
            }
            if (Array.isArray(resourceJson.contained) && resourceJson.contained.length > 0) {
                let containedResourceDictionary = {};
                for (const item of resourceJson.contained) {
                    const key = "#" + item.id;
                    containedResourceDictionary[key] = item;
                }
                setContainedResourceDictionary(containedResourceDictionary);
            }
        }
    }, [resourceJson]);

    useEffect(() => {
        if (versionAlgorithmDatatypeState === 'string' && resourceState.versionAlgorithmCoding) {
            setResourceState(prevState => { return { ...prevState, versionAlgorithmCoding: null } })
        }
        if (versionAlgorithmDatatypeState === 'Coding' && resourceState.versionAlgorithmString) {
            setResourceState(prevState => { return { ...prevState, versionAlgorithmString: null } })
        }
    }, [versionAlgorithmDatatypeState])

    useEffect(() => {
        if (editMode && resourceJson) {
            let newResourceDetail = JSON.parse(JSON.stringify(parentResourceDetail));
            let previousFinalValue = newResourceDetail.revisedResourceJson.title || null;
            newResourceDetail.revisedResourceJson.title = resourceState.title;
            newResourceDetail.displayTitle = resourceState.title ||
                (resourceState.name && resourceType !== "Practitioner") || newResourceDetail.displayTitle;
            if (resourceState.title !== undefined &&
                previousFinalValue !== resourceState.title) {
                console.log("resourceUpdate for " + resourceType + ".title");
                console.log("dispatch resourceUpdated"); dispatch(resourceUpdated(newResourceDetail));
            }
        }
    }, [resourceState.title]);

    useEffect(() => {
        if (editMode && resourceJson) {
            let newResourceDetail = JSON.parse(JSON.stringify(parentResourceDetail));
            let newTitle = extensionState.title || extensionState.name;
            if (newTitle && newTitle !== newResourceDetail.displayTitle) {
                newResourceDetail.displayTitle = newTitle;
                console.log("dispatch resourceUpdated"); dispatch(resourceUpdated(newResourceDetail));
            }
            if (!jsonEquals(extensionState, startingExtensionState)) {
                updateResourceFromExtensions(dispatch, extensionState,
                    resourceState, setResourceState, parentResourceDetail);
            }
        }
    }, [extensionState.title]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(resourceState, "subtitle", "string", parentResourceDetail,
                dispatch);
        }
    }, [resourceState.subtitle]);

    useEffect(() => {
        if (editMode && resourceJson) {
            if (resourceType === "Practitioner") {
                let newResourceDetail = JSON.parse(JSON.stringify(parentResourceDetail));
                let previousFinalValue = newResourceDetail.revisedResourceJson.name || null;
                newResourceDetail.revisedResourceJson.name = resourceState.name;
                if (resourceState.name?.[0] && displayStringFromFHIRHumanName(resourceState.name[0])) {
                    newResourceDetail.displayTitle =
                        displayStringFromFHIRHumanName(resourceState.name[0]);
                }
                if (resourceState.name !== undefined &&
                    !jsonEquals(previousFinalValue, resourceState.name)) {
                    console.log("resourceUpdate for Practitioner.name");
                    console.log("dispatch resourceUpdated");
                    dispatch(resourceUpdated(newResourceDetail));
                }
            } else {
                let newResourceDetail = JSON.parse(JSON.stringify(parentResourceDetail));
                let previousFinalValue = newResourceDetail.revisedResourceJson.name || null;
                newResourceDetail.revisedResourceJson.name = resourceState.name;
                newResourceDetail.displayTitle = resourceState.title ||
                    resourceState.name || newResourceDetail.displayTitle;
                if (resourceState.name !== undefined &&
                    previousFinalValue !== resourceState.name) {
                    console.log("resourceUpdate for " + resourceType + ".name");
                    console.log("dispatch resourceUpdated");
                    dispatch(resourceUpdated(newResourceDetail));
                }
            }
        }
    }, [resourceState.name]);

    useEffect(() => {
        if (editMode && resourceJson) {
            let newResourceDetail = JSON.parse(JSON.stringify(parentResourceDetail));
            let newTitle = extensionState.title || extensionState.name;
            if (newTitle && newTitle !== newResourceDetail.displayTitle) {
                newResourceDetail.displayTitle = newTitle;
                console.log("dispatch resourceUpdated"); dispatch(resourceUpdated(newResourceDetail));
            }
            if (!jsonEquals(extensionState, startingExtensionState)) {
                updateResourceFromExtensions(dispatch, extensionState,
                    resourceState, setResourceState, parentResourceDetail);
            }
        }
    }, [extensionState.name]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(resourceState, "url", "uri", parentResourceDetail,
                dispatch);
        }
    }, [resourceState.url]);

    useEffect(() => {
        if (!jsonEquals(extensionState, startingExtensionState)) {
            updateResourceFromExtensions(dispatch, extensionState,
                resourceState, setResourceState, parentResourceDetail);
        }
    }, [extensionState.url]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(resourceState, "identifier", "ArrayOfIdentifier", parentResourceDetail,
                dispatch);
        }
    }, [resourceState.identifier]);

    useEffect(() => {
        if (!jsonEquals(extensionState, startingExtensionState)) {
            updateResourceFromExtensions(dispatch, extensionState,
                resourceState, setResourceState, parentResourceDetail);
        }
    }, [extensionState.identifier]);

    useEffect(() => {
        if (editMode && resourceJson && parentResourceDetail.revisedResourceJson.meta && resourceState.meta &&
            !jsonEquals(parentResourceDetail.revisedResourceJson.meta.profile, resourceState.meta.profile)) {
            updateElement(resourceState, "meta", "Meta", parentResourceDetail,
                dispatch);
        }
    }, [resourceState.meta]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(resourceState, "version", "string", parentResourceDetail,
                dispatch);
        }
    }, [resourceState.version]);

    useEffect(() => {
        if (!jsonEquals(extensionState, startingExtensionState)) {
            updateResourceFromExtensions(dispatch, extensionState,
                resourceState, setResourceState, parentResourceDetail);
        }
    }, [extensionState.version]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(resourceState, "versionAlgorithmString", "string", parentResourceDetail,
                dispatch);
        }
    }, [resourceState.versionAlgorithmString]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(resourceState, "versionAlgorithmCoding", "Coding", parentResourceDetail,
                dispatch);
        }
    }, [resourceState.versionAlgorithmCoding]);

    useEffect(() => {
        if (!jsonEquals(extensionState, startingExtensionState)) {
            updateResourceFromExtensions(dispatch, extensionState,
                resourceState, setResourceState, parentResourceDetail);
        }
    }, [extensionState.versionAlgorithmString]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(resourceState, "status", "code", parentResourceDetail,
                dispatch);
        }
    }, [resourceState.status]);

    useEffect(() => {
        if (!jsonEquals(extensionState, startingExtensionState)) {
            updateResourceFromExtensions(dispatch, extensionState,
                resourceState, setResourceState, parentResourceDetail);
        }
    }, [extensionState.status]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(resourceState, "language", "code", parentResourceDetail,
                dispatch);
        }
    }, [resourceState.language]);

    useEffect(() => {
        if (!jsonEquals(extensionState, startingExtensionState)) {
            updateResourceFromExtensions(dispatch, extensionState,
                resourceState, setResourceState, parentResourceDetail);
        }
    }, [extensionState.additionalLanguage]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(resourceState, "experimental", "boolean", parentResourceDetail,
                dispatch);
        }
    }, [resourceState.experimental]);

    useEffect(() => {
        if (!jsonEquals(extensionState, startingExtensionState)) {
            updateResourceFromExtensions(dispatch, extensionState,
                resourceState, setResourceState, parentResourceDetail);
        }
    }, [extensionState.experimental]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(resourceState, "date", "dateTime", parentResourceDetail,
                dispatch);
        }
    }, [resourceState.date]);

    useEffect(() => {
        if (!jsonEquals(extensionState, startingExtensionState)) {
            updateResourceFromExtensions(dispatch, extensionState,
                resourceState, setResourceState, parentResourceDetail);
        }
    }, [extensionState.date]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(resourceState, "custodian", "Reference", parentResourceDetail,
                dispatch);
        }
    }, [resourceState.custodian]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(resourceState, "publisher", "string", parentResourceDetail,
                dispatch);
        }
    }, [resourceState.publisher]);

    useEffect(() => {
        if (!jsonEquals(extensionState, startingExtensionState)) {
            updateResourceFromExtensions(dispatch, extensionState,
                resourceState, setResourceState, parentResourceDetail);
        }
    }, [extensionState.publisher]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(resourceState, "contact", "ArrayOfContactDetail", parentResourceDetail,
                dispatch);
        }
    }, [resourceState.contact]);

    useEffect(() => {
        if (!jsonEquals(extensionState, startingExtensionState)) {
            updateResourceFromExtensions(dispatch, extensionState,
                resourceState, setResourceState, parentResourceDetail);
        }
    }, [extensionState.contact]);

    useEffect(() => {
        if (!jsonEquals(extensionState, startingExtensionState)) {
            updateResourceFromExtensions(dispatch, extensionState,
                resourceState, setResourceState, parentResourceDetail);
        }
    }, [extensionState.extendedContact]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(resourceState, "description", "markdown", parentResourceDetail,
                dispatch);
        }
    }, [resourceState.description]);

    useEffect(() => {
        if (!jsonEquals(extensionState, startingExtensionState)) {
            updateResourceFromExtensions(dispatch, extensionState,
                resourceState, setResourceState, parentResourceDetail);
        }
    }, [extensionState.description]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(resourceState, "useContext", "ArrayOfUsageContext", parentResourceDetail,
                dispatch);
        }
    }, [resourceState.useContext]);

    useEffect(() => {
        if (!jsonEquals(extensionState, startingExtensionState)) {
            updateResourceFromExtensions(dispatch, extensionState,
                resourceState, setResourceState, parentResourceDetail);
        }
    }, [extensionState.useContext]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(resourceState, "jurisdiction", "ArrayOfCodeableConcept", parentResourceDetail,
                dispatch);
        }
    }, [resourceState.jurisdiction]);

    useEffect(() => {
        if (!jsonEquals(extensionState, startingExtensionState)) {
            updateResourceFromExtensions(dispatch, extensionState,
                resourceState, setResourceState, parentResourceDetail);
        }
    }, [extensionState.jurisdiction]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(resourceState, "purpose", "markdown", parentResourceDetail,
                dispatch);
        }
    }, [resourceState.purpose]);

    useEffect(() => {
        if (!jsonEquals(extensionState, startingExtensionState)) {
            updateResourceFromExtensions(dispatch, extensionState,
                resourceState, setResourceState, parentResourceDetail);
        }
    }, [extensionState.purpose]);

    useEffect(() => {
        if (editMode && resourceJson) {
            if (resourceJson.resourceType === "Citation") {
                updateElement(resourceState, "classification", "ArrayOfCitationClassification", parentResourceDetail,
                    dispatch);
            } else if (resourceJson.resourceType === "Evidence") {
                updateElement(resourceState, "classification", "ArrayOfEvidenceClassification", parentResourceDetail,
                    dispatch);
            } else if (resourceJson.resourceType === "Composition") {
                updateElement(resourceState, "classification", "ArrayOfCompositionClassification", parentResourceDetail,
                    dispatch);
            }
        }
    }, [resourceState.classification]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(resourceState, "statusDate", "ArrayOfCitationStatusDate", parentResourceDetail,
                dispatch);
        }
    }, [resourceState.statusDate]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(resourceState, "usage", "markdown", parentResourceDetail,
                dispatch);
        }
    }, [resourceState.usage]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(resourceState, "note", "ArrayOfAnnotation", parentResourceDetail,
                dispatch);
        }
    }, [resourceState.note]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(resourceState, "currentState", "ArrayOfCodeableConcept", parentResourceDetail,
                dispatch);
        }
    }, [resourceState.currentState]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(resourceState, "copyright", "markdown", parentResourceDetail,
                dispatch);
        }
    }, [resourceState.copyright]);

    useEffect(() => {
        if (!jsonEquals(extensionState, startingExtensionState)) {
            updateResourceFromExtensions(dispatch, extensionState,
                resourceState, setResourceState, parentResourceDetail);
        }
    }, [extensionState.copyright]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(resourceState, "copyrightLabel", "string", parentResourceDetail,
                dispatch);
        }
    }, [resourceState.copyrightLabel]);

    useEffect(() => {
        if (!jsonEquals(extensionState, startingExtensionState)) {
            updateResourceFromExtensions(dispatch, extensionState,
                resourceState, setResourceState, parentResourceDetail);
        }
    }, [extensionState.copyrightLabel]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(resourceState, "approvalDate", "date", parentResourceDetail,
                dispatch);
        }
    }, [resourceState.approvalDate]);

    useEffect(() => {
        if (!jsonEquals(extensionState, startingExtensionState)) {
            updateResourceFromExtensions(dispatch, extensionState,
                resourceState, setResourceState, parentResourceDetail);
        }
    }, [extensionState.approvalDate]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(resourceState, "lastReviewDate", "date", parentResourceDetail,
                dispatch);
        }
    }, [resourceState.lastReviewDate]);

    useEffect(() => {
        if (!jsonEquals(extensionState, startingExtensionState)) {
            updateResourceFromExtensions(dispatch, extensionState,
                resourceState, setResourceState, parentResourceDetail);
        }
    }, [extensionState.lastReviewDate]);

    useEffect(() => {
        if (!jsonEquals(extensionState, startingExtensionState)) {
            updateResourceFromExtensions(dispatch, extensionState,
                resourceState, setResourceState, parentResourceDetail);
        }
    }, [extensionState.publicationDate]);

    useEffect(() => {
        if (!jsonEquals(extensionState, startingExtensionState)) {
            updateResourceFromExtensions(dispatch, extensionState,
                resourceState, setResourceState, parentResourceDetail);
        }
    }, [extensionState.publicationStatus]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(resourceState, "effectivePeriod", "Period", parentResourceDetail,
                dispatch);
        }
    }, [resourceState.effectivePeriod]);

    useEffect(() => {
        if (!jsonEquals(extensionState, startingExtensionState)) {
            updateResourceFromExtensions(dispatch, extensionState,
                resourceState, setResourceState, parentResourceDetail);
        }
    }, [extensionState.effectivePeriod]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(resourceState, "topic", "ArrayOfCodeableConcept", parentResourceDetail,
                dispatch);
        }
    }, [resourceState.topic]);

    useEffect(() => {
        if (!jsonEquals(extensionState, startingExtensionState)) {
            updateResourceFromExtensions(dispatch, extensionState,
                resourceState, setResourceState, parentResourceDetail);
        }
    }, [extensionState.topic]);

    useEffect(() => {
        if (editMode && resourceJson) {
            if (resourceType === "Composition") {
                updateElement(resourceState, "author", "ArrayOfReference", parentResourceDetail,
                    dispatch);
            } else {
                updateElement(resourceState, "author", "ArrayOfContactDetail", parentResourceDetail,
                    dispatch);
            }
        }
    }, [resourceState.author]);

    useEffect(() => {
        if (!jsonEquals(extensionState, startingExtensionState)) {
            updateResourceFromExtensions(dispatch, extensionState,
                resourceState, setResourceState, parentResourceDetail);
        }
    }, [extensionState.author]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(resourceState, "source", "ArrayOfContactDetail", parentResourceDetail,
                dispatch);
        }
    }, [resourceState.source]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(resourceState, "attester", "ArrayOfCompositionAttester", parentResourceDetail,
                dispatch);
        }
    }, [resourceState.attester]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(resourceState, "relatesTo", "ArrayOfRelatesTo", parentResourceDetail,
                dispatch);
        }
    }, [resourceState.relatesTo]);

    useEffect(() => {
        if (!jsonEquals(extensionState, startingExtensionState)) {
            updateResourceFromExtensions(dispatch, extensionState,
                resourceState, setResourceState, parentResourceDetail);
        }
    }, [extensionState.relatesTo]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(resourceState, "relatedArtifact", "ArrayOfRelatedArtifact", parentResourceDetail,
                dispatch);
        }
    }, [resourceState.relatedArtifact]);

    useEffect(() => {
        if (!jsonEquals(extensionState, startingExtensionState)) {
            updateResourceFromExtensions(dispatch, extensionState,
                resourceState, setResourceState, parentResourceDetail);
        }
    }, [extensionState.relatedArtifact]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(resourceState, "editor", "ArrayOfContactDetail", parentResourceDetail,
                dispatch);
        }
    }, [resourceState.editor]);

    useEffect(() => {
        if (!jsonEquals(extensionState, startingExtensionState)) {
            updateResourceFromExtensions(dispatch, extensionState,
                resourceState, setResourceState, parentResourceDetail);
        }
    }, [extensionState.editor]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(resourceState, "reviewer", "ArrayOfContactDetail", parentResourceDetail,
                dispatch);
        }
    }, [resourceState.reviewer]);

    useEffect(() => {
        if (!jsonEquals(extensionState, startingExtensionState)) {
            updateResourceFromExtensions(dispatch, extensionState,
                resourceState, setResourceState, parentResourceDetail);
        }
    }, [extensionState.reviewer]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(resourceState, "endorser", "ArrayOfContactDetail", parentResourceDetail,
                dispatch);
        }
    }, [resourceState.endorser]);

    useEffect(() => {
        if (!jsonEquals(extensionState, startingExtensionState)) {
            updateResourceFromExtensions(dispatch, extensionState,
                resourceState, setResourceState, parentResourceDetail);
        }
    }, [extensionState.endorser]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(resourceState, "recorder", "ArrayOfContactDetail", parentResourceDetail,
                dispatch);
        }
    }, [resourceState.recorder]);

    useEffect(() => {
        if (!jsonEquals(extensionState, startingExtensionState)) {
            updateResourceFromExtensions(dispatch, extensionState,
                resourceState, setResourceState, parentResourceDetail);
        }
    }, [extensionState.recorder]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(resourceState, "type", "CodeableConcept", parentResourceDetail,
                dispatch);
        }
    }, [resourceState.type]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(resourceState, "category", "ArrayOfCodeableConcept", parentResourceDetail,
                dispatch);
        }
    }, [resourceState.category]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(resourceState, "subject", "ArrayOfReference", parentResourceDetail,
                dispatch);
        }
    }, [resourceState.subject]);

    if (!resourceState || !resourceState.resourceType) {
        return <Spinner />;
    }

    return <div>
        {(resourceState.id && extensionState.otherExtensions) && <>
            <div>
                {metadataElementSettings.titleAsMetadataElement ?
                    <StringEntry elementName='title' fieldLabel='Title'
                        startingValue={resourceState.title} setResourceState={setResourceState} />
                    :
                    <StringEntry elementName='title' fieldLabel='Title'
                        startingValue={extensionState.title} setResourceState={setExtensionState} />
                }
                {(resourceType === 'ActivityDefinition' || resourceType === 'PlanDefinition') &&
                    <StringEntry elementName='subtitle' fieldLabel='Subtitle'
                        startingValue={resourceState.subtitle} setResourceState={setResourceState} />
                }
                {metadataElementSettings.nameAsMetadataElement ?
                    <>{(resourceType === "Practitioner") ?
                        <ArrayEntry datatype='HumanName' elementName='name' fieldLabel='Name' startCollapsed={true}
                            startingValue={resourceState.name} setResourceState={setResourceState} />
                        :
                        <StringEntry elementName='name' fieldLabel='Name'
                            startingValue={resourceState.name} setResourceState={setResourceState} />
                    }</>
                    :
                    <StringEntry elementName='name' fieldLabel='Name'
                        startingValue={extensionState.name} setResourceState={setExtensionState} />
                }
                {resourceType === "Composition" &&
                    <>
                        <CodeableConceptEntry elementName='type' fieldLabel='Type'
                            codeableConceptLevelValueSet={compositionTypeCodeableConceptSet} startCollapsed
                            startingValue={resourceState.type} setResourceState={setResourceState} />
                        <ArrayEntry datatype='CodeableConcept' elementName='category' fieldLabel='Category'
                            startEmptyArrayClosed={true} deletable={true} deletableArray={true} startCollapsed={true}
                            startingValue={resourceState.category} setResourceState={setResourceState} />
                        <ArrayEntry datatype='Reference' elementName='subject' fieldLabel='Subject' startCollapsed={true}
                            enableCreation={true}
                            startingValue={resourceState.subject} setResourceState={setResourceState} />
                    </>
                }
                {metadataElementSettings.urlAsMetadataElement ?
                    <TextField style={{ width: "100%", marginTop: "12px" }} multiline
                        className="inputField" type='text' label='URL' size="small" variant='outlined'
                        value={resourceState.url || ""}
                        onChange={(e) => {
                            setResourceState(prevState => { return { ...prevState, 'url': e.target.value }; });
                            setUrlChanged(true);
                        }} />
                    :
                    <TextField style={{ width: "100%", marginTop: "12px" }} multiline
                        className="inputField" type='text' label='URL' size="small" variant='outlined'
                        value={extensionState.url || ""}
                        onChange={(e) => {
                            setExtensionState(prevState => { return { ...prevState, 'url': e.target.value }; });
                            setUrlChanged(true);
                        }} />
                }
                {urlChanged && <p><b>Changing the URL is only desired if you want to set the CANONICAL url for this Resource!</b></p>}
                {metadataElementSettings.identifierAsMetadataElement ?
                    <ArrayEntry datatype='Identifier' elementName='identifier' fieldLabel='Identifier' startCollapsed={true}
                        containedResourceDictionary={containedResourceDictionary}
                        startingValue={resourceState.identifier} setResourceState={setResourceState} dataEntryStyle='preserveFOI' />
                    :
                    <ArrayEntry datatype='Identifier' elementName='identifier' fieldLabel='Identifier' startCollapsed={true}
                        containedResourceDictionary={containedResourceDictionary}
                        startingValue={extensionState.identifier} setResourceState={setExtensionState} dataEntryStyle='preserveFOI' />
                }
                <MetaProfileEntry resourceType={resourceType} startingValue={resourceState.meta} setResourceState={setResourceState} />
                {metadataElementSettings.versionAsMetadataElement ?
                    <StringEntry elementName='version' fieldLabel='Version'
                        startingValue={resourceState.version} setResourceState={setResourceState} />
                    :
                    <StringEntry elementName='version' fieldLabel='Version'
                        startingValue={extensionState.version} setResourceState={setExtensionState} />
                }
                {metadataElementSettings.versionAlgorithmAsMetadataElement ?
                    <>
                        <p style={{ marginBottom: "0px" }}><b>Version Algorithm:</b></p>
                        <div style={{ marginLeft: "24px" }}>
                            <DatatypeSelector elementXName='versonAlgorithm[x]' allowedDatatypes={versionAlgorithmAllowedDatatypes}
                                datatypeState={versionAlgorithmDatatypeState} setDatatypeState={setVersionAlgorithmDatatypeState} />
                            {versionAlgorithmDatatypeState === 'string' &&
                                <StringEntry elementName='versionAlgorithmString' fieldLabel={'String for Version Algorithm'}
                                    startingValue={resourceState.versionAlgorithmString} setResourceState={setResourceState} />}
                            {versionAlgorithmDatatypeState === 'Coding' &&
                                <CodingEntry elementName='versionAlgorithmCoding' fieldLabel={'Coding for Version Algorithm'}
                                    valueSet={versionAlgorithmValueSet}
                                    startingValue={resourceState.versionAlgorithmCoding} setResourceState={setResourceState} />}
                        </div>
                    </>
                    :
                    <StringEntry elementName='versionAlgorithmString' fieldLabel={'Version Algorithm'}
                        startingValue={extensionState.versionAlgorithmString} setResourceState={setExtensionState} />
                }
                {(resourceType === "Composition" || resourceType === "List") && <>
                    {metadataElementSettings.statusAsMetadataElement ?
                        <CodeEntry elementName='status' fieldLabel='Status' startingValue={resourceState.status}
                            setResourceState={setResourceState}
                            allowedValues={resourceType === "Composition" ? compositionDotStatusValues : metadataDotStatusValues} togglable={false} />
                        :
                        <CodeEntry elementName='status' fieldLabel='Status' startingValue={extensionState.status} setResourceState={setExtensionState}
                            allowedValues={metadataDotStatusValues} togglable={false} />
                    }
                </>}
                <br />
                <h4>Publication Status:</h4>
                <ResourceStatusRadioButton
                    foi={foi}
                    setJsonViewEditingState={setJsonViewEditingState}
                    setStatusChanged={setStatusChanged}
                />
                <CodeEntry elementName='language' fieldLabel='Language'
                    startingValue={resourceState.language} setResourceState={setResourceState}
                    allowedValues={languageCodes} />
                {metadataElementSettings.additionalLanguageAllowed &&
                    <CodeArrayEntry elementName="additionalLanguage" fieldLabel="Additional Language"
                        startingValue={extensionState.additionalLanguage} setResourceState={setExtensionState}
                        allowedValues={languageCodes} dataEntryStyle="dropdownsearch" />
                }
                {metadataElementSettings.experimentalAsMetadataElement ?
                    <BooleanEntry elementName='experimental' fieldLabel='Experimental'
                        startingValue={resourceState.experimental} setResourceState={setResourceState} />
                    :
                    <BooleanEntry elementName='experimental' fieldLabel='Experimental'
                        startingValue={extensionState.experimental} setResourceState={setExtensionState} />
                }
                {metadataElementSettings.dateAsMetadataElement ?
                    <DateTimeEntry elementName='date' fieldLabel='Date last changed'
                        startingValue={resourceState.date} setResourceState={setResourceState} />
                    :
                    <DateTimeEntry elementName='date' fieldLabel='Date last changed'
                        startingValue={extensionState.date} setResourceState={setExtensionState} />
                }
                {resourceType === "Composition" ?
                    <ReferenceEntry elementName='custodian' fieldLabel='Custodian/Publisher'
                        containedResourceDictionary={containedResourceDictionary}
                        startingValue={resourceState.custodian} setResourceState={setResourceState}
                        referencedResourceTypes={['Organization']} startCollapsed={true} />
                    :
                    <>
                        {metadataElementSettings.publisherAsMetadataElement ?
                            <StringEntry elementName='publisher' fieldLabel='Publisher'
                                startingValue={resourceState.publisher} setResourceState={setResourceState} />
                            :
                            <StringEntry elementName='publisher' fieldLabel='Publisher'
                                startingValue={extensionState.publisher} setResourceState={setExtensionState} />
                        }
                        {metadataElementSettings.contactAsMetadataElement ?
                            <ArrayEntry datatype='ContactDetail' elementName='contact'
                                fieldLabel='Publisher Contact' startCollapsed={true}
                                startingValue={resourceState.contact} setResourceState={setResourceState} />
                            :
                            <ArrayEntry datatype='ContactDetail' elementName='contact'
                                fieldLabel='Publisher Contact' startCollapsed={true}
                                startingValue={extensionState.contact} setResourceState={setExtensionState} />
                        }
                    </>
                }
                <ArrayEntry datatype='ExtendedContactDetail' elementName='extendedContact'
                    containedResourceDictionary={containedResourceDictionary}
                    fieldLabel='Extended Contact' startCollapsed={true}
                    startEmptyArrayClosed={true}
                    startingValue={extensionState.extendedContact} setResourceState={setExtensionState} />
                {metadataElementSettings.descriptionAsMetadataElement ?
                    <MarkdownEntry elementName='description' fieldLabel='Description'
                        startingValue={resourceState.description} setResourceState={setResourceState} />
                    :
                    <MarkdownEntry elementName='description' fieldLabel='Description'
                        startingValue={extensionState.description} setResourceState={setExtensionState} />
                }
                {metadataElementSettings.useContextAsMetadataElement ?
                    <ArrayEntry datatype='UsageContext' elementName='useContext' fieldLabel='Use Context'
                        containedResourceDictionary={containedResourceDictionary}
                        startingValue={resourceState.useContext} setResourceState={setResourceState} startCollapsed={true}
                        startEmptyArrayClosed={true} deletable={true} deletableArray={true} />
                    :
                    <ArrayEntry datatype='UsageContext' elementName='useContext' fieldLabel='Use Context'
                        containedResourceDictionary={containedResourceDictionary}
                        startingValue={extensionState.useContext} setResourceState={setExtensionState} startCollapsed={true}
                        startEmptyArrayClosed={true} deletable={true} deletableArray={true} />
                }
                {metadataElementSettings.jurisdictionAsMetadataElement ?
                    <ArrayEntry datatype='CodeableConcept' elementName='jurisdiction' fieldLabel='Jurisdiction'
                        startEmptyArrayClosed={true} deletable={true} deletableArray={true} startCollapsed={true}
                        startingValue={resourceState.jurisdiction} setResourceState={setResourceState} />
                    :
                    <ArrayEntry datatype='CodeableConcept' elementName='jurisdiction' fieldLabel='Jurisdiction'
                        startEmptyArrayClosed={true} deletable={true} deletableArray={true} startCollapsed={true}
                        startingValue={extensionState.jurisdiction} setResourceState={setExtensionState} />
                }
                {metadataElementSettings.purposeAsMetadataElement ?
                    <MarkdownEntry elementName='purpose' fieldLabel='Purpose'
                        startingValue={resourceState.purpose} setResourceState={setResourceState} />
                    :
                    <MarkdownEntry elementName='purpose' fieldLabel='Purpose'
                        startingValue={extensionState.purpose} setResourceState={setExtensionState} />
                }
                {resourceType === "Citation" &&
                    <>
                        <ArrayEntry datatype='CitationClassification' elementName='classification' fieldLabel='Classification'
                            startEmptyArrayClosed={true} deletable={true} deletableArray={true} startCollapsed={true}
                            startingValue={resourceState.classification} setResourceState={setResourceState} />
                        <ArrayEntry datatype='Annotation' elementName='note' fieldLabel='Note'
                            startEmptyArrayClosed={true} deletable={true} deletableArray={true} startCollapsed={true}
                            startingValue={resourceState.note} setResourceState={setResourceState} />
                        <ArrayEntry datatype='CodeableConcept' elementName='currentState' fieldLabel='Citation Status (Current State)'
                            startEmptyArrayClosed={true} deletable={true} deletableArray={true} startCollapsed={true}
                            startingValue={resourceState.currentState} setResourceState={setResourceState} />
                        <ArrayEntry datatype='CitationStatusDate' elementName='statusDate' fieldLabel='Citation Status (with dates)'
                            startEmptyArrayClosed={true} deletable={true} deletableArray={true} startCollapsed={true}
                            startingValue={resourceState.statusDate} setResourceState={setResourceState} />
                    </>
                }
                {(resourceType === "Evidence" || resourceType === "Composition") &&
                    <>
                        <ArrayEntry datatype='EvidenceClassification' elementName='classification' fieldLabel='Classification'
                            containedResourceDictionary={containedResourceDictionary}
                            startEmptyArrayClosed={true} deletable={true} deletableArray={true} startCollapsed={true}
                            startingValue={resourceState.classification} setResourceState={setResourceState} />
                    </>}
                {(resourceType === 'ActivityDefinition' || resourceType === 'PlanDefinition') &&
                    <MarkdownEntry elementName='usage' fieldLabel='Usage'
                        startingValue={resourceState.usage} setResourceState={setResourceState} />
                }
                {metadataElementSettings.copyrightAsMetadataElement ?
                    <MarkdownEntry elementName='copyright' fieldLabel='Copyright'
                        startingValue={resourceState.copyright} setResourceState={setResourceState} />
                    :
                    <MarkdownEntry elementName='copyright' fieldLabel='Copyright'
                        startingValue={extensionState.copyright} setResourceState={setExtensionState} />
                }
                {metadataElementSettings.copyrightLabelAsMetadataElement ?
                    <StringEntry elementName='copyrightLabel' fieldLabel='Copyright Label'
                        startingValue={resourceState.copyrightLabel} setResourceState={setResourceState} />
                    :
                    <StringEntry elementName='copyrightLabel' fieldLabel='Copyright Label'
                        startingValue={extensionState.copyrightLabel} setResourceState={setExtensionState} />
                }
                {metadataElementSettings.approvalDateAsMetadataElement ?
                    <DateEntry elementName='approvalDate' fieldLabel='Date approved'
                        startingValue={resourceState.approvalDate} setResourceState={setResourceState} />
                    :
                    <DateEntry elementName='approvalDate' fieldLabel='Date approved'
                        startingValue={extensionState.approvalDate} setResourceState={setExtensionState} />
                }
                {metadataElementSettings.lastReviewDateAsMetadataElement ?
                    <DateEntry elementName='lastReviewDate' fieldLabel='Date last reviewed'
                        startingValue={resourceState.lastReviewDate} setResourceState={setResourceState} />
                    :
                    <DateEntry elementName='lastReviewDate' fieldLabel='Date last reviewed'
                        startingValue={extensionState.lastReviewDate} setResourceState={setExtensionState} />
                }
                {metadataElementSettings.publicationDateAsMetadataElementAllowed &&
                    <DateEntry elementName='publicationDate' fieldLabel='Date published'
                        startingValue={extensionState.publicationDate} setResourceState={setExtensionState} />
                }
                {metadataElementSettings.publicationStatusAsMetadataElementAllowed &&
                    <CodeableConceptEntry elementName='publicationStatus' fieldLabel='Publication status'
                        codeableConceptLevelValueSet={convertValueSetToCodeableConceptSet("citedArtifactStatusTypeValueSet")}
                        startingValue={extensionState.publicationStatus} setResourceState={setExtensionState} />
                }
                {metadataElementSettings.effectivePeriodAsMetadataElement ?
                    <PeriodEntry elementName='effectivePeriod' fieldLabel='Effective period'
                        startingValue={resourceState.effectivePeriod} setResourceState={setResourceState} />
                    :
                    <PeriodEntry elementName='effectivePeriod' fieldLabel='Effective period'
                        startingValue={extensionState.effectivePeriod} setResourceState={setExtensionState} />
                }
                {metadataElementSettings.topicAsMetadataElement ?
                    <>{resourceState.topic &&
                        <>
                            <h3><b>The topic element is deprecated. Consider using useContext instead.</b></h3>
                            <ArrayEntry datatype='CodeableConcept' elementName='topic' fieldLabel='Topic'
                                startEmptyArrayClosed={true} deletable={true} deletableArray={true} startCollapsed={true}
                                startingValue={resourceState.topic} setResourceState={setResourceState} />
                        </>
                    }</>
                    :
                    <ArrayEntry datatype='CodeableConcept' elementName='topic' fieldLabel='Topic'
                        startEmptyArrayClosed={true} deletable={true} deletableArray={true} startCollapsed={true}
                        startingValue={extensionState.topic} setResourceState={setExtensionState} />
                }
                {metadataElementSettings.authorAsMetadataElement ?
                    <>
                        {resourceType === "Composition" ?
                            <ArrayEntry datatype='Reference' elementName='author' fieldLabel='Author' startCollapsed={true}
                                referencedResourceTypes={compositionDotAuthorResourceTypes}
                                startingValue={resourceState.author} setResourceState={setResourceState} />
                            :
                            <ArrayEntry datatype='ContactDetail' elementName='author' fieldLabel='Author' startCollapsed={true}
                                startingValue={resourceState.author} setResourceState={setResourceState} />
                        }
                    </>
                    :
                    <>
                        {resourceType === "List" ?
                            <ReferenceEntry datatype='ContactDetail' elementName='source' fieldLabel='Author' startCollapsed={true}
                                referencedResourceTypes={listDotSourceResourceTypes}
                                containedResourceDictionary={containedResourceDictionary}
                                startingValue={resourceState.source} setResourceState={setResourceState} />
                            :
                            <ArrayEntry datatype='ContactDetail' elementName='author' fieldLabel='Author' startCollapsed={true}
                                startingValue={extensionState.author} setResourceState={setExtensionState} />
                        }
                    </>
                }
                {resourceType === "Composition" ?
                    <>
                        <ArrayEntry datatype='Attester' elementName='attester' fieldLabel='Attester' startCollapsed={true}
                            containedResourceDictionary={containedResourceDictionary}
                            startEmptyArrayClosed={true} deletable={true} deletableArray={true}
                            startingValue={resourceState.attester} setResourceState={setResourceState} />
                        <ArrayEntry datatype='RelatesTo' elementName='relatesTo' fieldLabel='Related Item' startCollapsed={true}
                            containedResourceDictionary={containedResourceDictionary}
                            allowedTypeValues='FHIR' startEmptyArrayClosed={true} deletable={true} deletableArray={true}
                            startingValue={resourceState.relatesTo} setResourceState={setResourceState} />
                    </>
                    :
                    <>
                        {metadataElementSettings.editorAsMetadataElement ?
                            <ArrayEntry datatype='ContactDetail' elementName='editor' fieldLabel='Editor' startCollapsed={true}
                                startEmptyArrayClosed={true} deletable={true} deletableArray={true}
                                startingValue={resourceState.editor} setResourceState={setResourceState} />
                            :
                            <ArrayEntry datatype='ContactDetail' elementName='editor' fieldLabel='Editor' startCollapsed={true}
                                startEmptyArrayClosed={true} deletable={true} deletableArray={true}
                                startingValue={extensionState.editor} setResourceState={setExtensionState} />
                        }
                        {metadataElementSettings.reviewerAsMetadataElement ?
                            <ArrayEntry datatype='ContactDetail' elementName='reviewer' fieldLabel='Reviewer' startCollapsed={true}
                                startEmptyArrayClosed={true} deletable={true} deletableArray={true}
                                startingValue={resourceState.reviewer} setResourceState={setResourceState} />
                            :
                            <ArrayEntry datatype='ContactDetail' elementName='reviewer' fieldLabel='Reviewer' startCollapsed={true}
                                startEmptyArrayClosed={true} deletable={true} deletableArray={true}
                                startingValue={extensionState.reviewer} setResourceState={setExtensionState} />
                        }
                        {metadataElementSettings.endorserAsMetadataElement ?
                            <ArrayEntry datatype='ContactDetail' elementName='endorser' fieldLabel='Endorser' startCollapsed={true}
                                startEmptyArrayClosed={true} deletable={true} deletableArray={true}
                                startingValue={resourceState.endorser} setResourceState={setResourceState} />
                            :
                            <ArrayEntry datatype='ContactDetail' elementName='endorser' fieldLabel='Endorser' startCollapsed={true}
                                startEmptyArrayClosed={true} deletable={true} deletableArray={true}
                                startingValue={extensionState.endorser} setResourceState={setExtensionState} />
                        }
                        {metadataElementSettings.recorderAsMetadataElement ?
                            <ArrayEntry datatype='ContactDetail' elementName='recorder' fieldLabel='Recorder' startCollapsed={true}
                                startEmptyArrayClosed={true} deletable={true} deletableArray={true}
                                startingValue={resourceState.recorder} setResourceState={setResourceState} />
                            :
                            <ArrayEntry datatype='ContactDetail' elementName='recorder' fieldLabel='Recorder' startCollapsed={true}
                                startEmptyArrayClosed={true} deletable={true} deletableArray={true}
                                startingValue={extensionState.recorder} setResourceState={setExtensionState} />
                        }
                        {metadataElementSettings.relatesToAsMetadataElement ?
                            <ArrayEntry datatype='RelatesTo' elementName='relatesTo' fieldLabel='Related Item' startCollapsed={true}
                                containedResourceDictionary={containedResourceDictionary}
                                allowedTypeValues='FHIR' startEmptyArrayClosed={true} deletable={true} deletableArray={true}
                                startingValue={resourceState.relatesTo} setResourceState={setResourceState} />
                            :
                            <>{metadataElementSettings.relatedArtifactAsMetadataElement ?
                                <ArrayEntry datatype='RelatedArtifact' elementName='relatedArtifact' fieldLabel='Related Item' startCollapsed={true}
                                    containedResourceDictionary={containedResourceDictionary}
                                    allowedTypeValues='FHIR' startEmptyArrayClosed={true} deletable={true} deletableArray={true}
                                    startingValue={resourceState.relatedArtifact} setResourceState={setResourceState} />
                                :
                                <ArrayEntry datatype='RelatedArtifact' elementName='relatedArtifact' fieldLabel='Related Item' startCollapsed={true}
                                    containedResourceDictionary={containedResourceDictionary}
                                    allowedTypeValues='FHIR' startEmptyArrayClosed={true} deletable={true} deletableArray={true}
                                    startingValue={extensionState.relatedArtifact} setResourceState={setExtensionState} />
                            }</>
                        }
                    </>
                }
            </div>
        </>}
    </div>
}

export default MetadataPatternEdit;